/**
 * MapPreview — the office on a map, with its fence drawn to scale.
 * @file src/web/features/locations/components/MapPreview.tsx
 *
 * @llm-rule WHEN: Showing where an office is, or confirming a pick before saving
 * @llm-rule NOTE: OpenStreetMap's embed needs no API key, so this works the
 *   moment the app is installed. The Google key stays on the SERVER, where it
 *   is used for place SEARCH only — putting it in an embed URL would ship it to
 *   every browser.
 * @llm-rule AVOID: Treating the drawn circle as exact. It is scaled from the
 *   bounding box, which is honest to within a few percent at office distances.
 */

import { ExternalLink } from 'lucide-react';

/** Metres per degree of latitude. Constant enough for a fence this size. */
const M_PER_DEG_LAT = 111_320;

export function MapPreview({
  latitude,
  longitude,
  radiusM,
  height = 200,
  label,
}: {
  latitude: number;
  longitude: number;
  radiusM: number;
  height?: number;
  label?: string;
}) {
  // Frame the fence with room around it, so the circle never touches the edge.
  const spanM = Math.max(radiusM * 3.2, 220);
  const dLat = spanM / M_PER_DEG_LAT / 2;
  const dLng = dLat / Math.max(0.15, Math.cos((latitude * Math.PI) / 180));

  const bbox = [longitude - dLng, latitude - dLat, longitude + dLng, latitude + dLat]
    .map((n) => n.toFixed(6))
    .join(',');

  const src =
    `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}` +
    `&layer=mapnik&marker=${latitude.toFixed(6)},${longitude.toFixed(6)}`;

  // The circle is drawn over the tiles rather than by the map, because the
  // embed has no drawing API.
  //
  // Size it against HEIGHT, not width: the bbox's LATITUDE span is what maps
  // onto the frame's height, and that span is exactly `spanM` metres. Sizing by
  // width made the circle as wide as the frame on a landscape map, hiding the
  // tiles underneath it entirely.
  const circlePct = (radiusM * 2) / spanM;

  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-rule">
      <div className="relative bg-paper-sunk" style={{ height }}>
        <iframe
          title={label ? `Map of ${label}` : 'Office location map'}
          src={src}
          className="size-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer"
        />

        {/* The fence, to scale. */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            height: `${circlePct * 100}%`,
            aspectRatio: '1',
            background: 'color-mix(in oklch, var(--brand) 14%, transparent)',
            border: '2px solid color-mix(in oklch, var(--brand) 60%, transparent)',
          }}
        />
      </div>

      <div className="flex min-w-0 items-center justify-between gap-3 border-t border-rule bg-paper px-3 py-2">
        <span className="tnum truncate text-[11.5px] text-ink-faint">
          {latitude.toFixed(5)}, {longitude.toFixed(5)} · {radiusM} m fence
        </span>
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
          target="_blank"
          rel="noreferrer"
          className="flex flex-none items-center gap-1 text-[11.5px] font-medium text-brand hover:underline"
        >
          Google Maps
          <ExternalLink className="size-3" />
        </a>
      </div>
    </div>
  );
}
